/** Site-wide display preferences (from `system_settings`, public). */

export const DEFAULT_DISPLAY_TIMEZONE = 'Asia/Kolkata';

const DATE_FORMATS = new Set(['dmy', 'mdy', 'ymd', 'locale_in']);
const TIME_FORMATS = new Set(['24h', '12h']);

export function normalizeIanaTimeZone(raw) {
  const tz = String(raw || '').trim();
  if (!tz) return DEFAULT_DISPLAY_TIMEZONE;
  try {
    Intl.DateTimeFormat('en-US', { timeZone: tz });
    return tz;
  } catch {
    return DEFAULT_DISPLAY_TIMEZONE;
  }
}

export function normalizeDateFormat(raw) {
  const v = String(raw || '').trim().toLowerCase();
  return DATE_FORMATS.has(v) ? v : 'dmy';
}

export function normalizeTimeFormat(raw) {
  const v = String(raw || '').trim().toLowerCase();
  return TIME_FORMATS.has(v) ? v : '24h';
}

export function displayPrefsFromSettingsRows(rows) {
  const map = {};
  for (const row of rows || []) {
    if (row && row.key) map[row.key] = row.value;
  }
  return displayPrefsFromPublicMap(map);
}

export function displayPrefsFromPublicMap(map) {
  return {
    timeZone: normalizeIanaTimeZone(map?.site_display_timezone),
    dateFormat: normalizeDateFormat(map?.site_display_date_format),
    timeFormat: normalizeTimeFormat(map?.site_display_time_format),
  };
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

export function parseYmd(s) {
  const m = String(s || '').slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  return { y: +m[1], mo: +m[2], d: +m[3] };
}

export function formatDateParts(ymd, prefs) {
  if (!ymd) return '';
  const { y, mo, d } = ymd;
  const dd = pad2(d);
  const mm = pad2(mo);
  switch (prefs.dateFormat) {
    case 'mdy':
      return `${mm}/${dd}/${y}`;
    case 'ymd':
      return `${y}-${mm}-${dd}`;
    case 'locale_in': {
      try {
        const dt = new Date(Date.UTC(y, mo - 1, d, 12, 0, 0));
        return new Intl.DateTimeFormat('en-IN', {
          timeZone: prefs.timeZone,
          day: '2-digit',
          month: 'short',
          year: 'numeric',
        }).format(dt);
      } catch {
        return `${dd}/${mm}/${y}`;
      }
    }
    case 'dmy':
    default:
      return `${dd}/${mm}/${y}`;
  }
}

export function formatTimeFromHHMMSS(timeStr, prefs) {
  const t = String(timeStr || '').trim();
  const m = t.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/);
  if (!m) return t || '';
  let h = parseInt(m[1], 10);
  const min = parseInt(m[2], 10);
  const sec = m[3] != null ? parseInt(m[3], 10) : null;
  if (prefs.timeFormat === '12h') {
    const ampm = h >= 12 ? 'PM' : 'AM';
    let h12 = h % 12;
    if (h12 === 0) h12 = 12;
    const tail = sec != null && sec !== 0 ? `:${pad2(sec)}` : '';
    return `${h12}:${pad2(min)}${tail} ${ampm}`;
  }
  const hh = pad2(h);
  if (sec != null && sec !== 0) return `${hh}:${pad2(min)}:${pad2(sec)}`;
  return `${hh}:${pad2(min)}`;
}

/** Customer-selected pickup slot — always 12h AM/PM (matches booking site). */
export function formatCustomerPickupTime(timeStr, prefs) {
  return formatTimeFromHHMMSS(timeStr, { ...prefs, timeFormat: '12h' });
}

export function zonedCalendarParts(d, timeZone) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d);
  const get = (t) => parts.find((p) => p.type === t)?.value;
  return { y: +get('year'), mo: +get('month'), d: +get('day') };
}

function looksLikeInstantString(s) {
  const str = String(s || '').trim();
  if (!str) return false;
  if (/T|Z|[+-]\d{2}:?\d{2}$/.test(str)) return true;
  // Postgres-style "YYYY-MM-DD HH:mm:ss" / "YYYY-MM-DD HH:mm:ss.sss"
  if (/^\d{4}-\d{2}-\d{2}[ T]\d{1,2}:\d{2}/.test(str)) return true;
  return false;
}

/**
 * Calendar YYYY-MM-DD for trip pickup (matches DB business date). Handles pg DATE → JS Date
 * (UTC-off-by-one when sliced) by formatting in site timezone.
 */
export function normalizePickupCalendarYmd(raw, timeZone = DEFAULT_DISPLAY_TIMEZONE) {
  if (raw == null || raw === '') return '';
  if (typeof raw === 'string') {
    const t = raw.trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
  }
  const s = String(raw).trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  if (looksLikeInstantString(s) || raw instanceof Date) {
    const d = raw instanceof Date ? raw : new Date(s);
    if (!Number.isNaN(d.getTime())) {
      const { y, mo, d: day } = zonedCalendarParts(d, timeZone);
      return `${y}-${pad2(mo)}-${pad2(day)}`;
    }
  }
  const ymd = parseYmd(s);
  if (ymd) return `${ymd.y}-${pad2(ymd.mo)}-${pad2(ymd.d)}`;
  return s.slice(0, 10);
}

export function formatPickupDateTimeDisplay(pickupDate, pickupTime, prefs) {
  const tz = prefs?.timeZone || DEFAULT_DISPLAY_TIMEZONE;
  const calYmd = normalizePickupCalendarYmd(pickupDate, tz);
  const ymd = parseYmd(calYmd);
  const ds = ymd ? formatDateParts(ymd, prefs) : calYmd || '—';
  const ts = pickupTime ? formatCustomerPickupTime(pickupTime, prefs) : '';
  if (ds && ts && ds !== '—') return `${ds} · ${ts}`;
  return ts && ds === '—' ? ts : ds || ts || '—';
}

/** Pickup/service date only (site timezone), for booking list columns. */
export function formatBookingPickupDateDisplay(pickupDate, pickupTime, prefs) {
  const full = formatPickupDateTimeDisplay(pickupDate, pickupTime, prefs);
  if (full.includes(' · ')) return full.split(' · ')[0];
  return full;
}

export function formatZonedDate(d, prefs) {
  if (!d || Number.isNaN(d.getTime())) return '';
  const { y, mo, d: day } = zonedCalendarParts(d, prefs.timeZone);
  return formatDateParts({ y, mo, d: day }, prefs);
}

export function formatZonedTime(d, prefs, withSeconds = false) {
  if (!d || Number.isNaN(d.getTime())) return '';
  const hour12 = prefs.timeFormat === '12h';
  const opts = {
    timeZone: prefs.timeZone,
    hour: hour12 ? 'numeric' : '2-digit',
    minute: '2-digit',
    hour12,
  };
  if (withSeconds) opts.second = '2-digit';
  return new Intl.DateTimeFormat('en-IN', opts).format(d);
}

export function formatZonedDateTime(iso, prefs, withSeconds = false) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso || '').replace('T', ' ').slice(0, 19);
  const dateStr = formatZonedDate(d, prefs);
  const timeStr = formatZonedTime(d, prefs, withSeconds);
  if (!dateStr) return timeStr || '—';
  if (!timeStr) return dateStr;
  return `${dateStr} · ${timeStr}`;
}

export function formatTimelineCompact(iso, prefs) {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  const hour12 = prefs.timeFormat === '12h';
  return new Intl.DateTimeFormat('en-IN', {
    timeZone: prefs.timeZone,
    day: '2-digit',
    month: 'short',
    hour: hour12 ? 'numeric' : '2-digit',
    minute: '2-digit',
    hour12,
  }).format(d);
}

export function formatDisplayDateFromUnknown(raw, prefs) {
  const s = String(raw || '').trim();
  if (!s || s === '—') return '—';
  const tz = prefs?.timeZone || DEFAULT_DISPLAY_TIMEZONE;
  const cal = normalizePickupCalendarYmd(raw, tz);
  const ymd = parseYmd(cal);
  if (ymd) return formatDateParts(ymd, prefs);
  return s;
}
